import React, { useMemo, useState } from "react";
import { Alert, StyleSheet, Text, View } from "react-native";
import { useRouter } from "expo-router";
import * as Sentry from "@sentry/react-native";

import AttributeTag from "@/Components/Cards/AttributeTag";
import UserAvatar from "@/Components/Media/User/Images/UserAvatar";
import { formatDistanceStr } from "@/formatting/distance";
import { type AppUserSession } from "@/schemas/auth/session";
import { type UserProfile } from "@/schemas/user/profile";
import RelationshipService from "@/services/user/relationship";
import { calcIsUserOnline } from "@/utils/datetime";

type ProfileAvatarProps = {
  session: AppUserSession;
  profile: UserProfile;
};

function ProfileAvatar({
  session,
  profile,
}: ProfileAvatarProps): React.JSX.Element {
  const router = useRouter();
  const isViewSelfProfile = session.identity.username === profile.username;
  const [followerCount, setFollowerCount] = useState(profile.followerCount);
  const [isFollowing, setIsFollowing] = useState(profile.isFollowing);
  // TODO: Optimize state management
  // const [profileState, setProfileState] = useState({
  //   followerCount: profile.followerCount,
  //   isFollowing: profile.isFollowing,
  // });
  const [isProcessingFollowStatusChange, setIsProcessingFollowStatusChange] =
    useState(false);
  const connectionService = useMemo(
    () => new RelationshipService(session),
    [session.token], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const handleToggleFollowingButtonPress = async (): Promise<void> => {
    if (isProcessingFollowStatusChange) {
      return;
    }

    setIsProcessingFollowStatusChange(true);

    // Capture previous values before optimistic update
    const previousIsFollowing = isFollowing;
    const previousFollowerCount = followerCount;

    if (previousIsFollowing) {
      setIsFollowing(false);
      setFollowerCount(followerCount - 1);
    } else {
      setIsFollowing(true);
      setFollowerCount(followerCount + 1);
    }

    try {
      if (previousIsFollowing) {
        await connectionService.toUnFollow(profile.username);
      } else {
        await connectionService.toFollow(profile.username);
      }
    } catch (error) {
      Alert.alert("Something went wrong", "Please try again later.", [
        { text: "OK" },
      ]);
      Sentry.captureException(error);

      // Rollback to previous values
      setIsFollowing(previousIsFollowing);
      setFollowerCount(previousFollowerCount);
    } finally {
      setIsProcessingFollowStatusChange(false);
    }
  };

  const isOnline = calcIsUserOnline(profile.lastActivityTime);
  const separation = formatDistanceStr(profile.separation);

  const handleFollowerPress = (): void => {
    router.push({
      params: { username: profile.username },
      pathname: "/(main)/shared/followers",
    });
  };

  const handleFollowingPress = (): void => {
    router.push({
      params: { username: profile.username },
      pathname: "/(main)/shared/followings",
    });
  };

  return (
    <View style={styles.layout}>
      <UserAvatar
        avatar={profile.avatar}
        defaultAvatarColor={"white"}
        size={150}
        {...(!isViewSelfProfile && {
          profileFollow: {
            isFollowing: isFollowing,
            onPress: handleToggleFollowingButtonPress,
            isLoading: isProcessingFollowStatusChange,
          },
        })}
      />
      <Text style={styles.username}>@{profile.username}</Text>
      <View style={styles.tagsContainer}>
        <View style={styles.tagRow}>
          <AttributeTag
            icon={isOnline ? "radio-button-on" : "radio-button-off"}
            iconColor={isOnline ? "#2ECC71" : "#FF4136"}
            text={isOnline ? "Online" : "Offline"}
            backgroundColor="#2C3E50"
          />
          <AttributeTag
            icon="location"
            iconColor="#3498DB"
            text={separation}
            backgroundColor="#2C3E50"
          />
        </View>
        <View style={styles.tagRow}>
          <AttributeTag
            icon="people"
            iconColor="#2ECC71"
            text={`${followerCount} Followers`}
            backgroundColor="#2C3E50"
            onPress={handleFollowerPress}
          />
          <AttributeTag
            icon="people-outline"
            iconColor="#F39C12"
            text={`${profile.followingCount} Followings`}
            backgroundColor="#2C3E50"
            onPress={handleFollowingPress}
          />
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  layout: {
    alignItems: "center",
  },
  tagRow: {
    flexDirection: "row",
    justifyContent: "center",
  },
  tagsContainer: {
    top: 5,
    width: "100%",
  },
  username: {
    color: "rgba(255, 255, 255, 0.93)",
    fontSize: 20,
    fontWeight: "bold",
    marginTop: 5,
  },
});

export default ProfileAvatar;
